import { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { useT } from "@/lib/i18n";
import { HOME } from "@/constants/testIds";

// Deterministic size variation for a magazine-like layout
const SPANS = [
    "row-span-2",
    "",
    "",
    "row-span-2 col-span-2",
    "",
    "",
    "row-span-2",
    "",
    "col-span-2",
    "",
];

const Gallery = ({ items = [] }) => {
    const { lang } = useLang();
    const t = useT(lang);
    const [openIndex, setOpenIndex] = useState(null);

    const close = useCallback(() => setOpenIndex(null), []);
    const prev = useCallback(() => setOpenIndex((i) => (i === null ? null : (i - 1 + items.length) % items.length)), [items.length]);
    const next = useCallback(() => setOpenIndex((i) => (i === null ? null : (i + 1) % items.length)), [items.length]);

    useEffect(() => {
        if (openIndex === null) return;
        const onKey = (e) => {
            if (e.key === "Escape") close();
            if (e.key === "ArrowLeft") prev();
            if (e.key === "ArrowRight") next();
        };
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [openIndex, close, prev, next]);

    if (items.length === 0) return null;
    const active = openIndex === null ? null : items[openIndex];

    return (
        <section
            id="gallery"
            data-testid={HOME.gallerySection}
            className="relative py-28 md:py-40 overflow-hidden"
            style={{ background: "var(--bg-primary)" }}
        >
            <div className="mx-auto max-w-[1600px] px-6 md:px-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 mb-14 md:mb-20">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7 }}
                        className="md:col-span-6"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <span className="w-8 h-px" style={{ background: "var(--gold)" }} />
                            <span className="font-body text-[11px] uppercase tracking-[0.32em]" style={{ color: "var(--gold)" }}>
                                {t("gallery.eyebrow")}
                            </span>
                        </div>
                        <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight whitespace-pre-line" style={{ color: "var(--ink-1)" }}>
                            {t("gallery.title")}
                        </h2>
                    </motion.div>
                    <motion.p
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9, delay: 0.15 }}
                        className="md:col-span-5 md:col-start-8 self-end font-body text-lg md:text-xl leading-[1.55]"
                        style={{ color: "var(--ink-2)" }}
                    >
                        {t("gallery.desc")}
                    </motion.p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] md:auto-rows-[220px] gap-3 md:gap-5">
                    {items.map((item, i) => {
                        const caption = lang === "es" ? item.caption_es : item.caption_en;
                        return (
                            <motion.button
                                key={item.id}
                                type="button"
                                data-testid={HOME.galleryItem(item.id)}
                                onClick={() => setOpenIndex(i)}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.7, delay: (i % 4) * 0.06, ease: [0.22, 1, 0.36, 1] }}
                                whileHover={{ y: -6 }}
                                className={`group relative overflow-hidden rounded-2xl md:rounded-3xl text-left ${SPANS[i % SPANS.length]}`}
                                style={{ background: "#FFE4EE" }}
                            >
                                <img
                                    src={item.image}
                                    alt={caption}
                                    loading="lazy"
                                    className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
                                />
                                <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(46,16,37,0.6))" }} />
                                <div className="absolute bottom-0 inset-x-0 p-3 md:p-4 flex items-end justify-between gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                    <div>
                                        <div className="font-body text-[9px] uppercase tracking-[0.24em]" style={{ color: "#FFC9D9" }}>{item.theme}</div>
                                        <div className="font-serif text-white text-lg md:text-xl leading-tight mt-0.5 max-w-[36ch]">{caption}</div>
                                    </div>
                                    <span className="text-white text-lg">↗</span>
                                </div>
                                <span className="absolute top-3 left-3 font-body text-[9px] uppercase tracking-[0.24em] px-2 py-1 rounded-full backdrop-blur-md" style={{ background: "rgba(255,255,255,0.75)", color: "var(--ink-1)" }}>
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                            </motion.button>
                        );
                    })}
                </div>
            </div>

            {/* Lightbox */}
            <AnimatePresence>
                {active && (
                    <motion.div
                        key="lightbox"
                        data-testid={HOME.galleryLightbox}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[80] flex items-center justify-center p-6 md:p-12"
                        style={{ background: "rgba(20, 6, 16, 0.86)", backdropFilter: "blur(6px)" }}
                        onClick={close}
                    >
                        <button
                            data-testid={HOME.galleryClose}
                            onClick={close}
                            aria-label={t("gallery.close")}
                            className="absolute top-6 right-6 flex items-center gap-2 pl-3 pr-4 py-2 rounded-full font-body text-xs uppercase tracking-[0.24em]"
                            style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)" }}
                        >
                            <X size={14} /> {t("gallery.close")}
                        </button>
                        <button
                            data-testid={HOME.galleryPrev}
                            onClick={(e) => { e.stopPropagation(); prev(); }}
                            aria-label={t("gallery.prev")}
                            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center"
                            style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)" }}
                        >
                            <ChevronLeft />
                        </button>
                        <button
                            data-testid={HOME.galleryNext}
                            onClick={(e) => { e.stopPropagation(); next(); }}
                            aria-label={t("gallery.next")}
                            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center"
                            style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)" }}
                        >
                            <ChevronRight />
                        </button>

                        <motion.div
                            key={active.id}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            onClick={(e) => e.stopPropagation()}
                            className="relative max-w-[min(92vw,900px)] max-h-[86vh] flex flex-col items-center"
                        >
                            <img
                                src={active.image}
                                alt={lang === "es" ? active.caption_es : active.caption_en}
                                className="max-w-full max-h-[74vh] w-auto h-auto object-contain rounded-xl md:rounded-2xl"
                                style={{ boxShadow: "0 30px 60px -20px rgba(0,0,0,0.6)" }}
                            />
                            <div className="mt-4 text-center max-w-2xl">
                                <div className="font-body text-[10px] uppercase tracking-[0.28em]" style={{ color: "#FFC9D9" }}>{active.theme}</div>
                                <div className="font-serif text-2xl md:text-3xl text-white mt-1 leading-snug">
                                    {lang === "es" ? active.caption_es : active.caption_en}
                                </div>
                                <div className="font-body text-xs mt-3" style={{ color: "rgba(255,255,255,0.55)" }}>
                                    {openIndex + 1} / {items.length}
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default Gallery;
